import React from "react";
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Container,
  Box,
  Divider,
  Grid,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import transcribeLogo from "../../images/tranzcribe-logo-text-black.png";

// Import Material UI icons
import SummarizeIcon from "@mui/icons-material/Summarize";
import SettingsVoiceIcon from "@mui/icons-material/SettingsVoice";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";

const Homepage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <div>
      {/* Navigation Bar */}
      <AppBar
        position="static"
        sx={{
          backgroundColor: "transparent",
          boxShadow: "none",
          padding: { xs: 1, md: 2 },
        }}
      >
        <Toolbar
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          {/* Left-aligned Logo */}
          <Box sx={{ flex: 1, display: "flex", justifyContent: "flex-start" }}>
            <img
              src={transcribeLogo}
              alt={"transcribe-logo"}
              style={{ width: "200px", height: "auto" }}
            />
          </Box>

          {/* Right-aligned Buttons */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              marginTop: { xs: 1, sm: 0 },
            }}
          >
            <Button
              color="inherit"
              onClick={() => navigate("/login")}
              sx={{
                fontWeight: "500",
                textTransform: "none",
                color: "#333",
                marginRight: "16px",
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Log in
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => navigate("/start")}
              sx={{
                textTransform: "none",
                fontWeight: "500",
                backgroundColor: "#1976d2",
                "&:hover": { backgroundColor: "#1565c0" },
                fontSize: { xs: "0.875rem", sm: "1rem" },
              }}
            >
              Join our Beta
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container>
        <Box
          sx={{
            mt: { xs: 6, md: 10 },
            textAlign: "center",
            maxWidth: "700px",
            margin: "auto",
            px: { xs: 2, md: 0 },
          }}
        >
          <Typography
            variant={isSmallScreen ? "h4" : "h3"}
            gutterBottom
            sx={{ fontWeight: "300", color: "#333" }}
          >
            Speak & let technology work for you ✨
          </Typography>
          <Typography
            variant="h6"
            color="textSecondary"
            gutterBottom
            sx={{
              fontWeight: "400",
              color: "#555",
              marginBottom: "24px",
              fontSize: { xs: "1rem", sm: "1.25rem" },
            }}
          >
            Stop wasting time. Turn your voice into dynamic, AI-driven responses
            with Tranzcribe.
          </Typography>
          {/* Start for Free Button */}
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate("/start")}
            sx={{
              padding: "12px 24px",
              fontWeight: "500",
              backgroundColor: "#1976d2",
              "&:hover": { backgroundColor: "#1565c0" },
              fontSize: { xs: "0.9rem", sm: "1rem" },
            }}
          >
            Join Our Beta
          </Button>

          {/* Divider */}
          <Divider sx={{ my: { xs: 6, md: 10 } }} />

          {/* Sections with Icons */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            {/* Section 1: Summary */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <SummarizeIcon sx={{ fontSize: 40, color: "#1976d2" }} />
                <Typography
                  variant="h6"
                  sx={{ marginY: 2, fontWeight: "500", fontSize: "1.1rem" }}
                >
                  Automatic Summaries
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ fontSize: "0.95rem" }}
                >
                  Get key insights and action items instantly from any meeting
                  or conversation. Let Tranzcribe do the hard work for you.
                </Typography>
              </Box>
            </Grid>

            {/* Section 2: Voice Recognition */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <SettingsVoiceIcon sx={{ fontSize: 40, color: "#1976d2" }} />
                <Typography
                  variant="h6"
                  sx={{ marginY: 2, fontWeight: "500", fontSize: "1.1rem" }}
                >
                  Advanced Voice Recognition
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ fontSize: "0.95rem" }}
                >
                  Our state-of-the-art voice recognition technology captures
                  every word with crystal clarity, ensuring you don’t miss a
                  thing.
                </Typography>
              </Box>
            </Grid>

            {/* Section 3: Speed */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <SpeedIcon sx={{ fontSize: 40, color: "#1976d2" }} />
                <Typography
                  variant="h6"
                  sx={{ marginY: 2, fontWeight: "500", fontSize: "1.1rem" }}
                >
                  Speed & Efficiency
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ fontSize: "0.95rem" }}
                >
                  Tranzcribe is optimized for speed, delivering your transcripts
                  in record time, so you can focus on taking action.
                </Typography>
              </Box>
            </Grid>

            {/* Section 4: Security & Encryption */}
            <Grid item xs={12} sm={6} md={3}>
              <Box sx={{ textAlign: "center" }}>
                <SecurityIcon sx={{ fontSize: 40, color: "#1976d2" }} />
                <Typography
                  variant="h6"
                  sx={{ marginY: 2, fontWeight: "500", fontSize: "1.1rem" }}
                >
                  256-Bit Encryption
                </Typography>
                <Typography
                  variant="body1"
                  color="textSecondary"
                  sx={{ fontSize: "0.95rem" }}
                >
                  Your data is safe with us. Tranzcribe uses industry-leading
                  encryption to ensure your transcripts remain secure and
                  private.
                </Typography>
              </Box>
            </Grid>
          </Grid>

          {/* Additional Content */}
          <Typography
            variant="h6"
            gutterBottom
            sx={{
              fontWeight: "400",
              color: "#333",
              marginBottom: "24px",
              fontSize: { xs: "1.2rem", sm: "1.5rem" },
            }}
          >
            Tailored Transcriptions, Just for You
          </Typography>
          <Typography
            variant="body1"
            color="textSecondary"
            sx={{
              fontSize: { xs: "1rem", sm: "1.1rem" },
              marginBottom: 4,
              color: "#777",
            }}
          >
            Whether you're a content creator, journalist, student, or business
            professional, Tranzcribe adapts to your needs. Customize your
            documents, streamline your workflow, and elevate your productivity.
          </Typography>
        </Box>
      </Container>
    </div>
  );
};

export default Homepage;
